<template>
  <v-container fluid>
    <v-app-bar app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <v-btn icon @click="goBack" class="back-btn">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title><v-icon>mdi-view-dashboard</v-icon>
        &nbsp; Project Dashboard: {{ project.name }}</v-toolbar-title>
    </v-app-bar>

    <!-- Loading Spinner -->
    <v-row justify="center" align="center" v-if="loading">
      <v-progress-circular indeterminate color="primary" size="70" />
    </v-row>

    <!-- Table of Users (Grouped) -->
    <v-card
      v-if="!loading && groupedUserStats.length"
      class="mt-4 pa-4"
      outlined
    >
      <v-card-title><v-icon>mdi-chart-areaspline</v-icon>
        &nbsp; User Statistics </v-card-title>

      <v-data-table
        show-expand
        :items="groupedUserStats"
        :headers="groupedHeaders"
        item-value="user_id"
        expand-mode="multiple"
        v-model:expanded="expandedUsers"
        class="striped-rows elevation-2"
        density="compact"
      >
      <template #item.username="{ item }">
    <div class="d-flex align-center">
      <v-avatar size="32" class="me-2">
        <img
          :src="userImages[item.user_id]"
          alt="User Avatar"
          class="circle-image"
        />
      </v-avatar>
      <span>{{ item.username }}</span>
    </div>
  </template>
        <!-- Colored rectangles in parent row for statuses -->
        <template #item.not_transcribed="{ item }">
          <div
            class="status-rectangle"
            :style="{ backgroundColor: getStatusColor('not_transcribed') }"
          >
            {{ item.not_transcribed }}
          </div>
        </template>

        <template #item.transcribed="{ item }">
          <div
            class="status-rectangle"
            :style="{ backgroundColor: getStatusColor('transcribed') }"
          >
            {{ item.transcribed }}
          </div>
        </template>

        <template #item.to_retranscribe="{ item }">
          <div
            class="status-rectangle"
            :style="{ backgroundColor: getStatusColor('to_retranscribe') }"
          >
            {{ item.to_retranscribe }}
          </div>
        </template>

        <template #item.validated="{ item }">
          <div
            class="status-rectangle"
            :style="{ backgroundColor: getStatusColor('validated') }"
          >
            {{ item.validated }}
          </div>
        </template>

        <!-- The row that expands to show pages -->
        <template #expanded-row="{ item }">
          <tr>
            <td :colspan="groupedHeaders.length">
              <!-- Child table for the pages -->
              <v-data-table
                :items="item.pages"
                :headers="pageHeaders"
                item-value="page_id"
                class="child-table striped-rows"
              >
                <template #item.not_transcribed="{ item }">
                  <div
                    class="status-rectangle"
                    :style="{ backgroundColor: getStatusColor('not_transcribed') }"
                  >
                    {{ item.not_transcribed }}
                  </div>
                </template>
                <template #item.transcribed="{ item }">
                  <div
                    class="status-rectangle"
                    :style="{ backgroundColor: getStatusColor('transcribed') }"
                  >
                    {{ item.transcribed }}
                  </div>
                </template>
                <template #item.to_retranscribe="{ item }">
                  <div
                    class="status-rectangle"
                    :style="{ backgroundColor: getStatusColor('to_retranscribe') }"
                  >
                    {{ item.to_retranscribe }}
                  </div>
                </template>
                <template #item.validated="{ item }">
                  <div
                    class="status-rectangle"
                    :style="{ backgroundColor: getStatusColor('validated') }"
                  >
                    {{ item.validated }}
                  </div>
                </template>

                <!-- "Go to Page" button -->
                <template #item.action="{ item }">
                  <v-btn
    icon
    tile
    class="pa-1"
    v-if="item.page_id"
    @click.stop="goToPage(item.document_id, item.page_id)"
    style="width: 30px; height: 30px;"
  >
    <v-icon color="black" style="font-size: 24px;">
      mdi-subdirectory-arrow-right
    </v-icon>
  </v-btn>
                </template>
              </v-data-table>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- If no results -->
    <v-card v-else-if="!loading" class="mt-4 pa-4">
      <p>No user statistics found.</p>
    </v-card>
  </v-container>
</template>

<script>
import api from "@/plugins/axios";

export default {
  data() {
    return {
      userImages: {},
      project: {},
      loading: true,
      groupedUserStats: [],
      expandedUsers: [],
      // Parent table headers
      groupedHeaders: [
        { title: "",align: "center", value: "data-table-expand", width: 10 }, // expand toggle
        { title: "User Name", align: "center",value: "username", sortable: false },
        { title: "Total Pages",align: "center", value: "page_count", sortable: true },
        { title: "Not Transcribed",align: "center", value: "not_transcribed", sortable: true },
        { title: "Transcribed",align: "center", value: "transcribed", sortable: true },
        { title: "To Retranscribe",align: "center", value: "to_retranscribe", sortable: true },
        { title: "Validated", align: "center",value: "validated", sortable: true },
      ],
      // Child table headers
      pageHeaders: [
        { title: "Document Name",align: "center", value: "document_name", sortable: false },
        { title: "Page Name",align: "center", value: "page_name", sortable: false },
        { title: "Not Transcribed",align: "center", value: "not_transcribed", sortable: true },
        { title: "Transcribed",align: "center", value: "transcribed", sortable: true },
        { title: "To Retranscribe", align: "center",value: "to_retranscribe", sortable: true },
        { title: "Validated",align: "center", value: "validated", sortable: true },
        { title: "Go to Page",align: "center", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.global.current.dark;
    },
  },
  mounted() {
    this.fetchProject();
    this.fetchUserImages().then(() => {
    // after images are loaded, we can fetch stats
    this.fetchUserStatistics();
  });
  },
  methods: {
  async fetchUserImages() {
    try {
      const response = await api.get("/api/users/usernames-images/");
      // Response is an array like:
      // [ { id: 6, username: "xyja", image: "http://localhost:8000/media/user_images/xyjc.png" }, ... ]

      // Build a dictionary: userId -> imageUrl
      const defaultAvatarUrl = `${window.location.origin}/default-avatar.png`;
      response.data.forEach((user) => {
        this.userImages[user.id] = user.image ? user.image : defaultAvatarUrl;
      });
    } catch (error) {
      console.error("Error fetching user images:", error);
    }
  },
    async fetchProject() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/`);
        this.project = response.data;
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    },
    async fetchUserStatistics() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/user_statistics/`);
        this.groupedUserStats = this.transformUserStats(response.data);
      } catch (error) {
        console.error("Error fetching user statistics:", error);
      } finally {
        this.loading = false;
      }
    },
    // Aggregate stats per user, plus child page details
    transformUserStats(rawData) {
      return rawData.map((user) => {
        const { user_id, username, assigned_pages } = user;
        const userImage = this.userImages[user_id] || null;
        let totalNotTranscribed = 0;
        let totalTranscribed = 0;
        let totalRetranscribe = 0;
        let totalValidated = 0;

        // Prepare child page items
        const pageDetails = assigned_pages.map((page) => {
          const lineSummary = page.line_status_summary || {};
          const notTrans = lineSummary.not_transcribed || 0;
          const trans = lineSummary.transcribed || lineSummary.updated || 0;
          const reTrans = lineSummary.to_retranscribe || 0;
          const val = lineSummary.validated || 0;

          // Add to aggregates
          totalNotTranscribed += notTrans;
          totalTranscribed += trans;
          totalRetranscribe += reTrans;
          totalValidated += val;

          return {
            document_name: page.document_name,
            page_name: `Page ${page.page_number}`,
            page_id: page.page_id,
            document_id: page.document_id,
            not_transcribed: notTrans,
            transcribed: trans,
            to_retranscribe: reTrans,
            validated: val,
          };
        });

        return {
          user_id,
          username,
          // "Total Pages" for the user
          page_count: assigned_pages.length,
          not_transcribed: totalNotTranscribed,
          transcribed: totalTranscribed,
          to_retranscribe: totalRetranscribe,
          validated: totalValidated,
          pages: pageDetails,
        };
      });
    },
    goBack() {
      this.$router.push(`/projects/${this.project.id}`);
    },
    goToPage(documentId, pageId) {
      if (!documentId || !pageId) return;
      const projectId = this.project.id;
      this.$router.push(`/projects/${projectId}/documents/${documentId}/pages/${pageId}`);
    },
    getStatusColor(status) {
      const colors = {
        not_transcribed: "#E57373",    // Red
        transcribed: "#FDD835",       // Yellow
        validated: "#66BB6A",         // Green
        to_retranscribe: "#42A5F5",   // Blue
      };
      return colors[status] || "#E0E0E0";
    },
  },
};
</script>

<style scoped>
.back-btn {
  margin-left: 10px;
}

/* Colored rectangles */
.status-rectangle {
  width: 35px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
  cursor: default;
  margin: 0 auto; /* center horizontally */
}

/* Apply a background color on every other row for both parent & child tables */

:deep(tbody tr:nth-of-type(odd)) {
  background-color: rgba(0, 0, 0, 0.03);
}
.v-theme--DarkTheme :deep(tbody tr:nth-of-type(odd)) {
  background-color: rgba(255, 255, 255, 0.03);
}
:deep(.v-data-table th) {
  font-weight: bold !important;
  font-size: 14px;
}
.circle-image {
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: block;
}
.me-2 {
  margin-right: 8px;
}

</style>
